import React, { useState } from 'react';

import { useForm } from 'react-hook-form';

import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';

import { useRequestRestrictedPromo } from '~/hooks/apis/promo/useRequestRestrictedPromo';

import type { RequestRestrictedPromoPayload } from '~/services/PromoService';

type FriendsAndFamilyFormValues = {
  code: string;
  category: string;
  email: string;
  requesterFirstName: string;
  requesterLastName: string;
};

function optionalName(value: string): string | undefined {
  const trimmedValue = value.trim();
  return trimmedValue || undefined;
}

export function buildFriendsAndFamilyRequest(
  values: FriendsAndFamilyFormValues,
  brand: RequestRestrictedPromoPayload['brand'],
): RequestRestrictedPromoPayload {
  const requesterFirstName = optionalName(values.requesterFirstName);
  const requesterLastName = optionalName(values.requesterLastName);

  return {
    code: values.code.trim(),
    brand,
    category: values.category.trim(),
    email: values.email.trim(),
    ...(requesterFirstName ? { requesterFirstName } : {}),
    ...(requesterLastName ? { requesterLastName } : {}),
  };
}

function FriendsAndFamilyPromoTester({ tenant }: { tenant: App.Tenant }) {
  const requestRestrictedPromoMutation = useRequestRestrictedPromo();
  const isProduction = window.configs?.APP_ENV === 'production';
  const [submittedPayload, setSubmittedPayload] = useState<RequestRestrictedPromoPayload>();
  const { register, handleSubmit, formState } = useForm<FriendsAndFamilyFormValues>({
    defaultValues: {
      code: '',
      category: '',
      email: '',
      requesterFirstName: '',
      requesterLastName: '',
    },
  });

  const onSubmit = async (values: FriendsAndFamilyFormValues) => {
    const payload = buildFriendsAndFamilyRequest(values, tenant.brand as RequestRestrictedPromoPayload['brand']);
    setSubmittedPayload(payload);
    await requestRestrictedPromoMutation.mutateAsync(payload);
  };

  const mutationError = requestRestrictedPromoMutation.error;

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        {isProduction && (
          <Alert severity="warning">
            This is production. Submitting this form will trigger a real Friends &amp; Family promo code request.
          </Alert>
        )}
        <Typography variant="body2">
          Sends the same restricted-promo request as www-le-customer. It creates a Friends &amp; Family promo request;
          use a safe test recipient where appropriate.
        </Typography>
        <TextField
          {...register('code', { required: 'Promo code is required' })}
          error={Boolean(formState.errors.code)}
          fullWidth
          helperText={formState.errors.code?.message}
          label="Source promo code"
          required
        />
        <TextField
          {...register('category', { required: 'Category is required' })}
          error={Boolean(formState.errors.category)}
          fullWidth
          helperText="The category supplied by the source promo's customer flow."
          label="Category"
          required
        />
        <TextField
          {...register('email', {
            required: 'Invitee email is required',
            pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
          })}
          error={Boolean(formState.errors.email)}
          fullWidth
          helperText={formState.errors.email?.message}
          label="Invitee email"
          required
          type="email"
        />
        <Stack direction={{ sm: 'row' }} spacing={2}>
          <TextField {...register('requesterFirstName')} fullWidth label="Requester first name (optional)" />
          <TextField {...register('requesterLastName')} fullWidth label="Requester last name (optional)" />
        </Stack>
        <Stack direction="row" justifyContent="flex-start">
          <Button disabled={requestRestrictedPromoMutation.isPending} type="submit" variant="contained">
            {requestRestrictedPromoMutation.isPending ? 'Requesting…' : `Request for ${tenant.title}`}
          </Button>
        </Stack>
        {requestRestrictedPromoMutation.isSuccess && (
          <Alert severity="success">Restricted promo request accepted.</Alert>
        )}
        {mutationError && <Alert severity="error">{mutationError.message || 'Restricted promo request failed.'}</Alert>}
        {submittedPayload && (
          <Alert severity="info">
            Sent POST /api/promo/request-restricted-promo for <strong>{submittedPayload.email}</strong>.
          </Alert>
        )}
      </Stack>
    </Box>
  );
}

export default FriendsAndFamilyPromoTester;
